// redux/slices/calendarSettingsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl } from "../../Utils/serverSelector";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

/* ===================== THUNKS ===================== */

export const updateCalendarSettings = createAsyncThunk(
  "calendarSettings/updateCalendarSettings",
  async ({ uid, settings }, { rejectWithValue }) => {
    const logPrefix = "[CalendarSettings][UPDATE]";
    try {
      if (!uid) throw new Error("UID не передан");
      const res = await apiRequest("POST", "/calendar-settings", { uid, ...settings });
      const data = res?.data ?? res;

      return {
        uid: String(uid),
        settings:
          data?.isOnlineBookingEnabled !== undefined
            ? data
            : settings,
      };
    } catch (e) {
      const status = e?.response?.status;
      console.error(`${logPrefix} ERROR`, {
        uid,
        status,
        message: e?.message,
        responseData: e?.response?.data,
      });
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      return rejectWithValue(e?.response?.data || e?.message || "Failed to update calendar settings");
    }
  }
);

export const fetchCalendarSettings = createAsyncThunk(
  "calendarSettings/fetchCalendarSettings",
  async (uid, { rejectWithValue }) => {
    const logPrefix = "[CalendarSettings][FETCH]";

    try {

      if (!uid) {
        console.error(`${logPrefix} UID отсутствует`);
        throw new Error("UID не передан");
      }

      const url = `/calendar-settings/${encodeURIComponent(uid)}`;

      const res = await apiRequest("GET", url);

      /*
       * Проверяем оба варианта, потому что apiRequest может возвращать:
       * 1. axios response: { data: {...} }
       * 2. непосредственно данные: {...}
       */
      const data = res?.data ?? res;

      if (!data || typeof data !== "object") {
        console.error(`${logPrefix} Некорректный ответ`, {
          uid,
          data,
        });

        throw new Error(
          "Сервер не вернул настройки календаря"
        );
      }

      const result = {
        uid: String(uid),
        settings: data,
      };

      return result;
    } catch (e) {
      const status = e?.response?.status;

      console.error(`${logPrefix} ERROR`, {
        uid,
        message: e?.message,
        status,
        responseData: e?.response?.data,
        stack: e?.stack,
      });

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch (clearError) {
          console.error(
            `${logPrefix} CLEAR JWT ERROR`,
            clearError
          );
        }
      }

      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        "Failed to fetch calendar settings"
      );
    }
  }
);

export const fetchCalendarSettingsForUids = createAsyncThunk(
  "calendarSettings/fetchCalendarSettingsForUids",
  async (uidsArg, { getState, rejectWithValue }) => {
    try {
      const list = Array.isArray(uidsArg) ? uidsArg : [uidsArg];
      const unique = [...new Set(list.map((u) => String(u)).filter(Boolean))];
      if (unique.length === 0) return { results: [], requested: [] };

      const state = getState();
      const byUid = state?.calendarSettings?.byUid || {};
      const toFetch = unique.filter((uid) => !byUid[uid]);

      const results = [];
      const CONCURRENCY = 3;

      for (let i = 0; i < toFetch.length; i += CONCURRENCY) {
        const chunk = toFetch.slice(i, i + CONCURRENCY);
        const settled = await Promise.allSettled(
          chunk.map(async (uid) => {
            const res = await apiRequest("GET", `/calendar-settings/${uid}`);
            const data = res?.data;
            return { uid, settings: data };
          })
        );
        settled.forEach((r) => {
          if (r.status === "fulfilled" && r.value) results.push(r.value);
        });
      }

      return { results, requested: unique };
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      return rejectWithValue(e?.message || "Failed to fetch settings for uids");
    }
  }
);

export const fetchPublicCalendarSettings = createAsyncThunk(
  "calendarSettings/fetchPublicCalendarSettings",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const base = getServerUrl().replace(/\/$/, "");
      const { data } = await axios.get(`${base}/public/${encodeURIComponent(uid)}/calendar-settings/`);
      return { uid: String(uid), settings: data };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message || "Failed to fetch public calendar settings");
    }
  }
);

export const fetchPublicCalendarSettingsForUids = createAsyncThunk(
  "calendarSettings/fetchPublicCalendarSettingsForUids",
  async (uidsArg, { rejectWithValue }) => {
    try {
      const list = Array.isArray(uidsArg) ? uidsArg : [uidsArg];
      const unique = [...new Set(list.map((u) => String(u)).filter(Boolean))];
      if (unique.length === 0) return { results: [], requested: [] };

      const base = getServerUrl().replace(/\/$/, "");
      const results = [];
      const CONCURRENCY = 3;

      for (let i = 0; i < unique.length; i += CONCURRENCY) {
        const chunk = unique.slice(i, i + CONCURRENCY);
        const settled = await Promise.allSettled(
          chunk.map(async (uid) => {
            const { data } = await axios.get(`${base}/public/${encodeURIComponent(uid)}/calendar-settings/`);
            return { uid, settings: data };
          })
        );
        settled.forEach((r) => {
          if (r.status === "fulfilled" && r.value) results.push(r.value);
        });
      }

      return { results, requested: unique };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message || "Failed to fetch public settings for uids");
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  settings: {},
  status: "idle",
  error: null,
  byUid: {},
  statusByUid: {},
  errorByUid: {},
};

/* ===================== SLICE ===================== */

const calendarSettingsSlice = createSlice({
  name: "calendarSettings",
  initialState,
  reducers: {
    resetCalendarSettings(state) {
      state.settings = {};
      state.status = "idle";
      state.error = null;
      state.byUid = {};
      state.statusByUid = {};
      state.errorByUid = {};
    },
    // ✅ НОВЫЙ: напрямую кладём настройки в стор без HTTP-запроса
    // Используется в CancelAppointment после fetchPublicAppointmentPage
    setCalendarSettingsDirectly: (state, action) => {
      const { uid, settings } = action.payload || {};
      if (!uid || !settings) return;
      state.settings = settings;
      state.byUid[uid] = settings;
      state.statusByUid[uid] = "succeeded";
      state.errorByUid[uid] = null;
      state.status = "succeeded";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    /* ===== fetchCalendarSettings (single, PRIVATE) ===== */
    builder
      .addCase(fetchCalendarSettings.pending, (state, action) => {
        const uid = String(action.meta.arg);
        state.status = "loading";
        state.statusByUid[uid] = "loading";
        state.errorByUid[uid] = null;
      })
      .addCase(fetchCalendarSettings.fulfilled, (state, action) => {
        const { uid, settings } = action.payload || {};
        if (!uid) return;
        state.status = "succeeded";
        state.settings = settings || {};
        state.byUid[uid] = settings || {};
        state.statusByUid[uid] = "succeeded";
        state.error = null;
        state.errorByUid[uid] = null;
      })
      .addCase(fetchCalendarSettings.rejected, (state, action) => {
        const uid = String(action.meta.arg);
        const msg = action.payload || action.error?.message || "Failed to fetch calendar settings";
        state.status = "failed";
        state.error = msg;
        state.statusByUid[uid] = "failed";
        state.errorByUid[uid] = msg;
      });

    /* ===== updateCalendarSettings (single, PRIVATE) ===== */
    builder
      .addCase(updateCalendarSettings.pending, (state, action) => {
        const uid = String(action.meta.arg?.uid);
        state.status = "loading";
        if (uid) state.statusByUid[uid] = "loading";
      })
      .addCase(updateCalendarSettings.fulfilled, (state, action) => {
        const { uid, settings } = action.payload || {};
        state.status = "succeeded";
        state.settings = settings || {};
        if (uid) {
          state.byUid[uid] = settings || {};
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
        state.error = null;
      })
      .addCase(updateCalendarSettings.rejected, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");
        const msg = action.payload || action.error?.message || "Failed to update calendar settings";
        state.status = "failed";
        state.error = msg;
        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
        }
      });

    /* ===== fetchCalendarSettingsForUids (batch, PRIVATE) ===== */
    builder
      .addCase(fetchCalendarSettingsForUids.pending, (state, action) => {
        state.byUid ||= {};
        state.statusByUid ||= {};
        state.errorByUid ||= {};
        const uids = Array.isArray(action.meta.arg) ? action.meta.arg : [action.meta.arg];
        uids.map(String).filter(Boolean).forEach((uid) => {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        });
      })
      .addCase(fetchCalendarSettingsForUids.fulfilled, (state, action) => {
        state.byUid ||= {};
        state.statusByUid ||= {};
        state.errorByUid ||= {};
        const { results = [], requested = [] } = action.payload || {};
        results.forEach(({ uid, settings }) => {
          const k = String(uid);
          state.byUid[k] = settings || {};
          state.statusByUid[k] = "succeeded";
          state.errorByUid[k] = null;
        });
        requested.map(String).forEach((uid) => {
          if (state.statusByUid[uid] === "loading") {
            state.statusByUid[uid] = state.byUid[uid] ? "succeeded" : "failed";
          }
        });
      })
      .addCase(fetchCalendarSettingsForUids.rejected, (state, action) => {
        state.statusByUid ||= {};
        state.errorByUid ||= {};
        const uids = Array.isArray(action.meta.arg) ? action.meta.arg : [action.meta.arg];
        const msg = action.payload || action.error?.message || "Failed to fetch settings";
        uids.map(String).filter(Boolean).forEach((uid) => {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
        });
      });

    /* ===== fetchPublicCalendarSettings ===== */
    builder
      .addCase(fetchPublicCalendarSettings.pending, (state, action) => {
        const uid = String(action.meta.arg);
        state.status = "loading";
        state.statusByUid[uid] = "loading";
        state.errorByUid[uid] = null;
      })
      .addCase(fetchPublicCalendarSettings.fulfilled, (state, action) => {
        const { uid, settings } = action.payload || {};
        if (!uid) return;
        state.status = "succeeded";
        state.settings = settings || {};
        state.byUid[uid] = settings || {};
        state.statusByUid[uid] = "succeeded";
        state.error = null;
        state.errorByUid[uid] = null;
      })
      .addCase(fetchPublicCalendarSettings.rejected, (state, action) => {
        const uid = String(action.meta.arg);
        const msg = action.payload || action.error?.message || "Failed to fetch public calendar settings";
        state.status = "failed";
        state.error = msg;
        state.statusByUid[uid] = "failed";
        state.errorByUid[uid] = msg;
      });

    /* ===== fetchPublicCalendarSettingsForUids ===== */
    builder
      .addCase(fetchPublicCalendarSettingsForUids.pending, (state, action) => {
        const uids = Array.isArray(action.meta.arg) ? action.meta.arg : [action.meta.arg];
        uids.map(String).filter(Boolean).forEach((uid) => {
          state.statusByUid[uid] = "loading";
          state.errorByUid[uid] = null;
        });
      })
      .addCase(fetchPublicCalendarSettingsForUids.fulfilled, (state, action) => {
        const { results = [], requested = [] } = action.payload || {};
        results.forEach(({ uid, settings }) => {
          const k = String(uid);
          state.byUid[k] = settings || {};
          state.statusByUid[k] = "succeeded";
          state.errorByUid[k] = null;
        });
        requested.map(String).forEach((uid) => {
          if (state.statusByUid[uid] === "loading") {
            state.statusByUid[uid] = state.byUid[uid] ? "succeeded" : "failed";
          }
        });
      })
      .addCase(fetchPublicCalendarSettingsForUids.rejected, (state, action) => {
        const uids = Array.isArray(action.meta.arg) ? action.meta.arg : [action.meta.arg];
        const msg = action.payload || action.error?.message || "Failed to fetch public settings";
        uids.map(String).filter(Boolean).forEach((uid) => {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
        });
      });
  },
});

export const { resetCalendarSettings, setCalendarSettingsDirectly } = calendarSettingsSlice.actions;
export default calendarSettingsSlice.reducer;

/* ===================== SELECTORS ===================== */

export const selectCalendarSettings = (s) => s.calendarSettings.settings;

export const selectCalendarSettingsByUid = (s, uid) =>
  s.calendarSettings.byUid?.[String(uid)] || null;

export const selectIsOnlineBookingEnabledByUid = (s, uid) =>
  Boolean(s.calendarSettings.byUid?.[String(uid)]?.isOnlineBookingEnabled);

export const selectRestrictNewUsersByUid = (s, uid) =>
  Boolean(s.calendarSettings.byUid?.[String(uid)]?.restrictNewUsers);

export const selectCalendarSettingsStatusByUid = (s, uid) =>
  s.calendarSettings.statusByUid?.[String(uid)] || "idle";

export const selectCalendarSettingsErrorByUid = (s, uid) =>
  s.calendarSettings.errorByUid?.[String(uid)] || null;
